import React, { useEffect, useId, useState } from 'react';
import { helpCenterSearch } from '@/apis/helpCenterAPI';
import { AIChatSearchIcon, AiStar } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import type {
  HelpSearchProps,
  HelpSearchResponse,
  HelpSearchResult,
} from '../../types';
import styles from '../../AppHelpCenter.module.scss';
import { useHelpEvent } from '../../hooks';
import HelpSearchResultRow from '../HelpSearchResultRow/HelpSearchResultRow';

type SearchOption = { key: string; run: () => void };

const HelpSearch = ({
  onOpenArticle,
  onAskAssistant,
  onAskInsightChat,
  aiEnabled,
  insightChatEnabled,
}: HelpSearchProps) => {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [response, setResponse] = useState<HelpSearchResponse | null>(null);
  const { token } = useToken();
  const trackEvent = useHelpEvent();
  const listboxId = useId();

  const trimmed = query.trim();
  const open = focused && !!response;
  const isDataIntent = response?.intent === 'data';
  // PRD: Insight Chat row needs Insight Chat access; assistant row needs AI on.
  // Without Insight Chat, a data question falls back to the assistant row (5.5).
  const showInsight = insightChatEnabled && isDataIntent;
  const showAssistant = aiEnabled && !showInsight;
  const results = response?.results ?? [];

  useEffect(() => {
    if (!trimmed) {
      setResponse(null);
      return undefined;
    }

    // Waits for a typing pause and drops responses for an older query.
    let cancelled = false;
    const timer = setTimeout(() => {
      const model = {
        search: trimmed,
      };
      helpCenterSearch(token, model)
        .then((res) => {
          if (cancelled) return;
          const data = res?.data?.data?.data ?? null;
          setResponse(data);
          if (data) {
            trackEvent({
              event: 'help_search',
              props: {
                query: trimmed,
                result_count: data.results.length,
                intent: data.intent,
              },
            });
          }
        })
        .catch(() => {
          if (!cancelled) setResponse(null);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, trimmed, trackEvent]);

  const openArticle = (result: HelpSearchResult) => {
    // PRD: opening an article clears the query.
    setQuery('');
    trackEvent({
      event: 'help_result_open',
      props: { article_id: result.id, rank: results.indexOf(result) + 1 },
    });
    onOpenArticle(result);
  };

  // A question handed to Insight Chat or the assistant is used up too.
  const handOff = (send: (question: string) => void) => {
    setQuery('');
    send(trimmed);
  };

  // Keyboard order matches render order: Insight Chat row first (data
  // intent), then articles, then the assistant row (never for data intent).
  const options: SearchOption[] = [
    ...(showInsight
      ? [{ key: 'insight', run: () => handOff(onAskInsightChat) }]
      : []),
    ...results.map((result) => ({
      key: result.id,
      run: () => openArticle(result),
    })),
    ...(showAssistant
      ? [{ key: 'assistant', run: () => handOff(onAskAssistant) }]
      : []),
  ];
  const optionId = (key: string) => `${listboxId}-${key}`;
  const activeKey = options[activeIndex]?.key;

  // First option is highlighted by default, so Enter takes the Insight Chat
  // row for data questions, the top article otherwise, and the assistant row
  // when nothing matched.
  useEffect(() => {
    setActiveIndex(0);
  }, [response]);

  useEffect(() => {
    if (!open || !activeKey) return;
    document
      .getElementById(optionId(activeKey))
      ?.scrollIntoView({ block: 'nearest' });
    // optionId is derived from the stable listboxId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeKey]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || options.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + options.length) % options.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      options[activeIndex]?.run();
    }
  };

  const optionProps = (key: string, index: number, run: () => void) => ({
    id: optionId(key),
    role: 'option',
    'aria-selected': activeIndex === index,
    onMouseEnter: () => setActiveIndex(index),
    onClick: run,
  });

  const resultOffset = showInsight ? 1 : 0;
  const assistantIndex = options.length - 1;

  return (
    <div className={styles.helpSearch}>
      <div
        className={`${styles.helpSearchField} ${
          focused ? styles.helpSearchFieldFocused : ''
        }`}
      >
        <span className={styles.helpSearchIcon}>
          <AIChatSearchIcon width={24} height={24} color='currentColor' />
        </span>
        <input
          type='text'
          className={styles.helpSearchInput}
          placeholder='Search or ask a question'
          aria-label='Search or ask a question'
          role='combobox'
          aria-autocomplete='list'
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && activeKey ? optionId(activeKey) : undefined
          }
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {open && (
        // preventDefault keeps focus in the input while clicking a row.
        <div
          id={listboxId}
          role='listbox'
          aria-label='Search results'
          className={styles.helpSearchDropdown}
          onMouseDown={(event) => event.preventDefault()}
        >
          {showInsight && (
            <div
              {...optionProps('insight', 0, options[0].run)}
              className={`${styles.helpSearchHandoff} ${
                activeIndex === 0 ? styles.helpSearchHandoffActive : ''
              }`}
            >
              <span className={styles.helpSearchHandoffIcon}>
                <AiStar color='currentColor' size={16} />
              </span>
              <span className={styles.helpSearchHandoffText}>
                <span className={styles.helpSearchHandoffTitle}>
                  Ask Insight Chat: {trimmed}
                </span>
                <span className={styles.helpSearchHandoffSubtitle}>
                  Looks like a question about your data
                </span>
              </span>
            </div>
          )}

          <div className={styles.helpSearchResults}>
            {showInsight && results.length > 0 && (
              <div className={styles.helpSearchDivider} role='presentation'>
                Or read how it works
              </div>
            )}

            {results.map((result, index) => (
              <HelpSearchResultRow
                key={result.id}
                id={optionId(result.id)}
                result={result}
                active={activeIndex === index + resultOffset}
                onHover={() => setActiveIndex(index + resultOffset)}
                onSelect={() => openArticle(result)}
              />
            ))}

            {!showInsight && results.length === 0 && (
              <div className={styles.helpSearchEmpty} role='presentation'>
                {aiEnabled
                  ? 'No articles match. Ask the assistant instead.'
                  : 'No articles match.'}
              </div>
            )}

            {response?.hidden_match && (
              <div className={styles.helpSearchHidden} role='presentation'>
                Some articles are hidden for your role
              </div>
            )}
          </div>

          {showAssistant && (
            <div
              {...optionProps(
                'assistant',
                assistantIndex,
                options[assistantIndex].run
              )}
              className={`${styles.helpSearchHandoff} ${
                activeIndex === assistantIndex
                  ? styles.helpSearchHandoffActive
                  : ''
              }`}
            >
              <span className={styles.helpSearchHandoffIcon}>
                <AiStar color='currentColor' size={16} />
              </span>
              <span className={styles.helpSearchHandoffText}>
                <span className={styles.helpSearchHandoffTitle}>
                  Ask: {trimmed}
                </span>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpSearch;
